<script setup lang="ts">
// Floating "contact us on LINE" button. Rests as a small round icon; a press
// opens it into a labelled pill, and the label is what opens LINE (the logo
// closes it again, as does scrolling on). It can be dragged along either screen
// edge when it covers something, and remembers where it was left on this device.
const OA_URL = 'https://line.me/R/ti/p/@362kemvk'
const STORAGE_KEY = 'line-fab-position'
const SIZE = 56
const MARGIN = 16
const DRAG_THRESHOLD = 6
// Keep clear of the header at the top and the mobile tab bar at the bottom.
const TOP_CLEARANCE = 72
const BOTTOM_CLEARANCE_MOBILE = 80
const BOTTOM_CLEARANCE_DESKTOP = 24

const { t } = useI18n()
const route = useRoute()

// Staff work screens, sign-in flows and check-in kiosks don't need it.
const HIDDEN_PREFIXES = ['/login', '/signup', '/auth', '/callback', '/forgot-password', '/confirm-signup', '/verify-email', '/app/organizers', '/app/admin', '/app/judge', '/app/checkin']
const hidden = computed(() => HIDDEN_PREFIXES.some(p => route.path === p || route.path.startsWith(`${p}/`)))

// On a phone the cookie-consent banner fills the bottom of the screen; stay
// out from under it until the visitor has answered.
const { gaMeasurementId } = useRuntimeConfig().public
const consent = useAnalyticsConsent()
const consentPending = computed(() => !!gaMeasurementId && consent.value === null)

const side = ref<'left' | 'right'>('right')
const bottom = ref(BOTTOM_CLEARANCE_DESKTOP)
// Open = the pill with its label. Every page starts with it closed.
const open = ref(false)
let scrollAtOpen = 0
const dragging = ref(false)
const dragPos = ref({ x: 0, y: 0 })

const expanded = computed(() => open.value && !dragging.value)

// documentElement.client* is the layout viewport; window.inner* grows when a
// page overflows sideways on phones and would push the button off-screen.
function viewport() {
  return { w: document.documentElement.clientWidth, h: document.documentElement.clientHeight }
}

function minBottom() {
  return viewport().w < 768 ? BOTTOM_CLEARANCE_MOBILE : BOTTOM_CLEARANCE_DESKTOP
}

function clampBottom(value: number) {
  const max = Math.max(minBottom(), viewport().h - TOP_CLEARANCE - SIZE)
  return Math.min(Math.max(value, minBottom()), max)
}

function restore() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null') as { side?: string, bottom?: number } | null
    if (saved?.side === 'left' || saved?.side === 'right') side.value = saved.side
    bottom.value = clampBottom(typeof saved?.bottom === 'number' ? saved.bottom : minBottom())
  } catch {
    bottom.value = clampBottom(minBottom())
  }
}

function save() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ side: side.value, bottom: bottom.value }))
  } catch { /* private mode: it just isn't remembered */ }
}

// Scrolling on means the visitor is reading again: close out of the way.
function onScroll() {
  if (open.value && Math.abs(window.scrollY - scrollAtOpen) > 24) open.value = false
}

function onResize() {
  bottom.value = clampBottom(bottom.value)
}

onMounted(() => {
  restore()
  window.addEventListener('scroll', onScroll, { passive: true })
  window.addEventListener('resize', onResize)
})
onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll)
  window.removeEventListener('resize', onResize)
})

// A new page starts closed again.
watch(() => route.path, () => {
  open.value = false
})

// ── Drag ──
// A press that moves past the threshold is a drag (the button follows the
// pointer and the click is swallowed); anything shorter stays a normal tap.
let start: { x: number, y: number, offsetX: number, offsetY: number } | null = null
let suppressClick = false
// Whether the pill was open when the press began.
let openAtPress = false
// Whether the press began on the logo. Decided at pointerdown because pointer
// capture retargets the click to the button itself.
let pressedLogo = false

function onPointerDown(e: PointerEvent) {
  if (e.button !== 0) return
  // Mouse only: stop the browser turning the press into a native link drag,
  // which cancels the pointer stream. Touch is covered by touch-action: none.
  if (e.pointerType === 'mouse') e.preventDefault()
  openAtPress = expanded.value
  pressedLogo = !!(e.target as Element).closest('[data-fab-logo]')
  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
  start = { x: e.clientX, y: e.clientY, offsetX: e.clientX - rect.left, offsetY: e.clientY - rect.top }
  ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
}

function onPointerMove(e: PointerEvent) {
  if (!start) return
  if (!dragging.value) {
    if (Math.hypot(e.clientX - start.x, e.clientY - start.y) < DRAG_THRESHOLD) return
    dragging.value = true
    // The pill collapses to the round icon while dragging; keep it under the pointer.
    start.offsetX = Math.min(start.offsetX, SIZE / 2)
    start.offsetY = Math.min(start.offsetY, SIZE / 2)
  }
  dragPos.value = {
    x: Math.min(Math.max(e.clientX - start.offsetX, 0), viewport().w - SIZE),
    y: Math.min(Math.max(e.clientY - start.offsetY, 0), viewport().h - SIZE)
  }
}

function onPointerUp() {
  if (dragging.value) {
    side.value = dragPos.value.x + SIZE / 2 < viewport().w / 2 ? 'left' : 'right'
    bottom.value = clampBottom(viewport().h - dragPos.value.y - SIZE)
    save()
    suppressClick = true
    dragging.value = false
  }
  start = null
}

// A drag swallows the click. A press on the closed button opens it; on the
// open pill the logo closes it again and only the label goes through to LINE.
function onClick(e: MouseEvent) {
  if (suppressClick) {
    e.preventDefault()
    suppressClick = false
    return
  }
  if (!openAtPress) {
    e.preventDefault()
    open.value = true
    scrollAtOpen = window.scrollY
  } else if (pressedLogo) {
    e.preventDefault()
    open.value = false
  }
}

const positionStyle = computed(() => dragging.value
  ? { left: `${dragPos.value.x}px`, top: `${dragPos.value.y}px` }
  : { [side.value]: `${MARGIN}px`, bottom: `${bottom.value}px` })
</script>

<template>
  <ClientOnly>
    <a
      v-if="!hidden"
      :href="OA_URL"
      target="_blank"
      rel="noopener"
      draggable="false"
      data-testid="line-fab"
      :data-side="side"
      :data-expanded="expanded"
      :aria-label="t('contact.fabLabel')"
      :aria-expanded="expanded"
      :title="t('contact.fabLabel')"
      class="fixed z-40 flex items-center h-14 rounded-full bg-[#06C755] hover:bg-[#05b34c] text-white shadow-lg select-none touch-none print:hidden"
      :class="[
        dragging ? 'cursor-grabbing shadow-2xl scale-105' : 'cursor-pointer transition-[left,right,bottom,transform] duration-200',
        side === 'left' ? 'flex-row' : 'flex-row-reverse',
        consentPending ? 'max-md:hidden' : ''
      ]"
      :style="positionStyle"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
      @dragstart.prevent
      @click="onClick"
    >
      <span
        class="flex items-center justify-center w-14 h-14 shrink-0"
        data-testid="line-fab-logo"
        data-fab-logo
      >
        <UIcon
          name="i-simple-icons-line"
          class="w-7 h-7"
        />
      </span>
      <span
        class="overflow-hidden whitespace-nowrap font-semibold transition-[max-width,padding] duration-200"
        :class="[
          expanded ? 'max-w-48' : 'max-w-0',
          expanded ? (side === 'left' ? 'pr-5' : 'pl-5') : ''
        ]"
      >{{ t('contact.fabLabel') }}</span>
    </a>
  </ClientOnly>
</template>
